export const QUERY_MATCH_RANK_NONE = 0;
export const QUERY_MATCH_RANK_FUZZY = 1;
export const QUERY_MATCH_RANK_SUBSTRING = 2;
export const QUERY_MATCH_RANK_PREFIX = 3;
export const QUERY_MATCH_RANK_EXACT = 4;

const NO_QUERY_MATCH = Object.freeze({
  rank: QUERY_MATCH_RANK_NONE,
  score: 0,
  matchMode: "none",
  windowStart: null,
  windowEnd: null,
  matchedValue: "",
});
const QUERY_MATCH_CACHEABLE_VALUE_MAX_LENGTH = 1024;
const PREPARED_COMPACT_QUERY_TOKENS = new WeakMap();
const QUERY_MATCH_VALUES_RESULT_LIMIT = 4;
const QUERY_MATCH_VALUES_CACHE = new WeakMap();
const EMPTY_PREPARED_QUERY_MATCH_VALUE = Object.freeze({
  raw: "",
  normalized: "",
  slug: "",
  compact: "",
  tokens: Object.freeze([]),
  compactTokens: Object.freeze([]),
});

function asciiFold(value) {
  const text = String(value ?? "");
  return typeof text.normalize === "function"
    ? text.normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
    : text;
}

export function normalizeQueryMatchText(value) {
  return String(value ?? "").trim().toLowerCase().replace(/\s+/g, " ");
}

export function slugifyQueryMatchText(value) {
  return asciiFold(normalizeQueryMatchText(value))
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function compactQueryMatchText(value) {
  return asciiFold(normalizeQueryMatchText(value)).replace(/[^a-z0-9]/g, "");
}

export function queryMatchTokens(value) {
  return asciiFold(normalizeQueryMatchText(value)).match(/\p{L}+|\p{N}+/gu) || [];
}

function preparedQueryMatchValue(value) {
  const raw = String(value ?? "").trim();
  if (!raw) {
    return EMPTY_PREPARED_QUERY_MATCH_VALUE;
  }
  const normalized = normalizeQueryMatchText(raw);
  const folded = asciiFold(normalized);
  const tokens = Object.freeze(folded.match(/\p{L}+|\p{N}+/gu) || []);
  return Object.freeze({
    raw,
    normalized,
    slug: folded.replace(/['"]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, ""),
    compact: folded.replace(/[^a-z0-9]/g, ""),
    tokens,
    compactTokens: Object.freeze(
      tokens.map((token) => token.replace(/[^a-z0-9]/g, "")),
    ),
  });
}

export function flattenQueryMatchValues(values) {
  return (Array.isArray(values) ? values : [values])
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .filter((value) => value != null);
}

function sameStringValues(left, right) {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

function queryMatchValuesCacheEntry(values) {
  if (!Array.isArray(values)) {
    const flattened = flattenQueryMatchValues(values);
    return {
      preparedValues: flattened.map((value) => preparedQueryMatchValue(value)),
      results: null,
      cacheable: false,
    };
  }
  // This matcher is a public primitive, so do not assume every caller uses the
  // immutable catalog projections from the search runtime. The small string
  // snapshot keeps same-identity edits correct and is much cheaper than running
  // every normalization and fuzzy matcher again for an unchanged query.
  const snapshot = flattenQueryMatchValues(values).map((value) => String(value ?? "").trim());
  const cached = QUERY_MATCH_VALUES_CACHE.get(values);
  if (cached && sameStringValues(cached.snapshot, snapshot)) {
    return cached;
  }
  const entry = {
    snapshot,
    preparedValues: snapshot.map((value) => preparedQueryMatchValue(value)),
    results: new Map(),
    cacheable: snapshot.every(
      (value) => value.length <= QUERY_MATCH_CACHEABLE_VALUE_MAX_LENGTH,
    ),
  };
  QUERY_MATCH_VALUES_CACHE.set(values, entry);
  return entry;
}

function queryMatchValuesResultKey(query, options) {
  if (query && typeof query === "object") {
    return null;
  }
  const optionKeys = Object.keys(options);
  if (optionKeys.some((key) => key !== "allowFuzzy" && key !== "slug")) {
    return null;
  }
  const rawQuery = String(query ?? "");
  const slug = options.slug == null ? null : String(options.slug || "");
  if (
    rawQuery.length > QUERY_MATCH_CACHEABLE_VALUE_MAX_LENGTH
    || (slug != null && slug.length > QUERY_MATCH_CACHEABLE_VALUE_MAX_LENGTH)
  ) {
    return null;
  }
  return JSON.stringify([
    rawQuery,
    slug,
    options.allowFuzzy !== false,
  ]);
}

function cachedQueryMatchValuesResult(entry, key) {
  if (!entry.cacheable || !entry.results || key == null) {
    return null;
  }
  const result = entry.results.get(key);
  if (!result) {
    return null;
  }
  entry.results.delete(key);
  entry.results.set(key, result);
  return { ...result };
}

function cacheQueryMatchValuesResult(entry, key, result) {
  if (!entry.cacheable || !entry.results || key == null) {
    return;
  }
  entry.results.set(key, Object.freeze({ ...result }));
  if (entry.results.size > QUERY_MATCH_VALUES_RESULT_LIMIT) {
    entry.results.delete(entry.results.keys().next().value);
  }
}

export function compactSubsequenceMatches(haystack, needle) {
  const source = String(haystack || "");
  const query = String(needle || "");
  if (!source || !query || query.length <= 1) {
    return false;
  }
  let cursor = 0;
  for (const char of query) {
    cursor = source.indexOf(char, cursor);
    if (cursor < 0) {
      return false;
    }
    cursor += 1;
  }
  return true;
}

function compactSubsequenceSpan(haystack, needle) {
  const source = String(haystack || "");
  const query = String(needle || "");
  if (!source || !query || query.length <= 1) {
    return null;
  }
  let previousIndex = -1;
  let firstIndex = -1;
  let gapPenalty = 0;
  for (const char of query) {
    const index = source.indexOf(char, previousIndex + 1);
    if (index < 0) {
      return null;
    }
    if (firstIndex < 0) {
      firstIndex = index;
    } else {
      gapPenalty += Math.max(0, index - previousIndex - 1);
    }
    previousIndex = index;
  }
  return {
    start: firstIndex,
    end: previousIndex + 1,
    gapPenalty,
    spanPenalty: Math.max(0, previousIndex - firstIndex + 1 - query.length),
  };
}

export function prepareQueryMatchState(value, options = {}) {
  const prepared = preparedQueryMatchValue(value);
  return options.slug == null
    ? prepared
    : Object.freeze({ ...prepared, slug: String(options.slug || "") });
}

function preparedStringList(value, path) {
  if (!Array.isArray(value) || value.some((entry) => typeof entry !== "string")) {
    throw new TypeError(`${path} must be an array of strings`);
  }
  return Object.freeze([...value]);
}

export function immutablePreparedQueryMatchValues(values, path = "prepared match values") {
  if (!Array.isArray(values)) {
    throw new TypeError(`${path} must be an array`);
  }
  return Object.freeze(values.map((value, index) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      throw new TypeError(`${path}[${index}] must be an object`);
    }
    const strings = {};
    for (const key of ["raw", "normalized", "slug", "compact"]) {
      if (typeof value[key] !== "string") {
        throw new TypeError(`${path}[${index}].${key} must be a string`);
      }
      strings[key] = value[key];
    }
    return Object.freeze({
      ...strings,
      tokens: preparedStringList(value.tokens, `${path}[${index}].tokens`),
      compactTokens: preparedStringList(
        value.compactTokens,
        `${path}[${index}].compactTokens`,
      ),
    });
  }));
}

export function createPreparedQueryMatchCacheTransaction(assignments) {
  if (!Array.isArray(assignments)) {
    throw new TypeError("prepared match cache assignments must be an array");
  }
  const preparedAssignments = assignments.flatMap((assignment, index) => {
    const entry = assignment?.entry;
    if (!entry || (typeof entry !== "object" && typeof entry !== "function")) {
      return [];
    }
    const cache = assignment?.cache;
    if (
      typeof cache?.get !== "function" ||
      typeof cache?.has !== "function" ||
      typeof cache?.set !== "function" ||
      typeof cache?.delete !== "function"
    ) {
      throw new TypeError(`prepared match cache assignment ${index} has no cache`);
    }
    return [{
      cache,
      entry,
      value: immutablePreparedQueryMatchValues(
        assignment.values,
        assignment.label || `prepared match cache assignment ${index}`,
      ),
    }];
  });
  let previousValues = null;
  return Object.freeze({
    commit() {
      if (previousValues) {
        return;
      }
      const applied = [];
      try {
        for (const assignment of preparedAssignments) {
          const previous = {
            ...assignment,
            present: assignment.cache.has(assignment.entry),
            value: assignment.cache.get(assignment.entry),
          };
          assignment.cache.set(assignment.entry, assignment.value);
          applied.push(previous);
        }
        previousValues = applied;
      } catch (error) {
        for (const previous of applied.reverse()) {
          if (previous.present) {
            previous.cache.set(previous.entry, previous.value);
          } else {
            previous.cache.delete(previous.entry);
          }
        }
        throw error;
      }
    },
    rollback() {
      if (!previousValues) {
        return;
      }
      for (const previous of previousValues.reverse()) {
        if (previous.present) {
          previous.cache.set(previous.entry, previous.value);
        } else {
          previous.cache.delete(previous.entry);
        }
      }
      previousValues = null;
    },
  });
}

function noMatch() {
  return { ...NO_QUERY_MATCH };
}

function candidateMatch({
  rank,
  score,
  matchMode,
  matchedValue,
  windowStart = null,
  windowEnd = null,
  indexStart = null,
  indexEnd = null,
}) {
  return {
    rank,
    score,
    matchMode,
    windowStart,
    windowEnd,
    matchedValue: String(matchedValue ?? ""),
    indexStart,
    indexEnd,
  };
}

function betterMatch(left, right) {
  if (!right || right.rank <= QUERY_MATCH_RANK_NONE) {
    return left;
  }
  if (!left || left.rank <= QUERY_MATCH_RANK_NONE) {
    return right;
  }
  if (right.rank !== left.rank) {
    return right.rank > left.rank ? right : left;
  }
  if (right.score !== left.score) {
    return right.score > left.score ? right : left;
  }
  const leftWindow = Number.isInteger(left.windowStart) ? left.windowStart : Number.MAX_SAFE_INTEGER;
  const rightWindow = Number.isInteger(right.windowStart) ? right.windowStart : Number.MAX_SAFE_INTEGER;
  if (rightWindow !== leftWindow) {
    return rightWindow < leftWindow ? right : left;
  }
  const leftLength = String(left.matchedValue || "").length;
  const rightLength = String(right.matchedValue || "").length;
  return rightLength < leftLength ? right : left;
}

function compactTokenMatchScore(candidate, query, allowFuzzy) {
  if (!candidate || !query) {
    return null;
  }
  if (candidate === query) {
    return 140;
  }
  if (candidate.startsWith(query)) {
    return 125 - Math.min(candidate.length - query.length, 40);
  }
  const index = candidate.indexOf(query);
  if (index >= 0) {
    return 90 - Math.min(index, 60);
  }
  if (!allowFuzzy || query.length <= 1 || !compactSubsequenceMatches(candidate, query)) {
    return null;
  }
  return 40;
}

function orderedPreparedQueryTokenMatch(candidateTokens, queryTokens, options = {}) {
  if (!candidateTokens.length || !queryTokens.length) {
    return null;
  }
  let best = null;
  for (let start = 0; start < candidateTokens.length; start += 1) {
    let cursor = start;
    let score = 0;
    let firstIndex = -1;
    let lastIndex = -1;
    let failed = false;
    for (const queryToken of queryTokens) {
      let tokenMatch = null;
      for (let index = cursor; index < candidateTokens.length; index += 1) {
        const candidateScore = compactTokenMatchScore(
          candidateTokens[index],
          queryToken,
          options.allowFuzzy === true,
        );
        if (candidateScore == null) {
          continue;
        }
        const adjustedScore = candidateScore - Math.max(0, index - cursor) * 12;
        if (
          !tokenMatch ||
          adjustedScore > tokenMatch.score ||
          (adjustedScore === tokenMatch.score && index < tokenMatch.index)
        ) {
          tokenMatch = { index, score: adjustedScore };
        }
        if (candidateScore >= 120) {
          break;
        }
      }
      if (!tokenMatch) {
        failed = true;
        break;
      }
      if (firstIndex < 0) {
        firstIndex = tokenMatch.index;
      }
      lastIndex = tokenMatch.index;
      score += tokenMatch.score;
      cursor = tokenMatch.index + 1;
    }
    if (failed || firstIndex < 0) {
      continue;
    }
    const match = {
      score:
        score -
        Math.max(0, lastIndex - firstIndex + 1 - queryTokens.length) * 10 -
        firstIndex * 24,
      windowStart: firstIndex,
      windowEnd: lastIndex + 1,
    };
    if (
      !best ||
      match.score > best.score ||
      (match.score === best.score && match.windowStart < best.windowStart)
    ) {
      best = match;
    }
  }
  return best;
}

export function orderedQueryTokenMatch(candidateTokens, queryTokens, options = {}) {
  return orderedPreparedQueryTokenMatch(
    candidateTokens.map((token) => preparedQueryMatchValue(token).compact),
    queryTokens.map((token) => preparedQueryMatchValue(token).compact),
    options,
  );
}

function orderedCompactPreparedQueryTokenMatch(candidateTokens, query, preparedTokens = null) {
  if (candidateTokens.length < 2 || query.length <= 1) {
    return null;
  }
  let tokens = preparedTokens || (Object.isFrozen(candidateTokens)
    ? PREPARED_COMPACT_QUERY_TOKENS.get(candidateTokens)
    : null);
  if (!tokens) {
    tokens = candidateTokens.map((token) => preparedQueryMatchValue(token).compact);
    if (Object.isFrozen(candidateTokens)) {
      PREPARED_COMPACT_QUERY_TOKENS.set(candidateTokens, tokens);
    }
  }
  if (tokens.length < 2) {
    return null;
  }
  let best = null;
  const firstQueryCode = query.charCodeAt(0);
  for (let start = 0; start < tokens.length - 1; start += 1) {
    if (tokens[start].charCodeAt(0) !== firstQueryCode) {
      continue;
    }
    let queryIndex = 0;
    let score = 0;
    let firstIndex = -1;
    let lastIndex = -1;
    let matchedTokenCount = 0;
    for (let index = start; index < tokens.length && queryIndex < query.length; index += 1) {
      const candidate = tokens[index];
      const available = Math.min(candidate.length, query.length - queryIndex);
      let take = 0;
      while (
        take < available &&
        candidate.charCodeAt(take) === query.charCodeAt(queryIndex + take)
      ) {
        take += 1;
      }
      if (take <= 0) {
        continue;
      }
      if (firstIndex < 0) {
        firstIndex = index;
      }
      lastIndex = index;
      matchedTokenCount += 1;
      score += 110 + take * 8 - Math.min(candidate.length - take, 30);
      queryIndex += take;
    }
    if (queryIndex !== query.length || firstIndex < 0 || matchedTokenCount < 2) {
      continue;
    }
    const match = {
      score:
        score -
        Math.max(0, lastIndex - firstIndex + 1 - matchedTokenCount) * 10 -
        firstIndex * 24,
      windowStart: firstIndex,
      windowEnd: lastIndex + 1,
    };
    if (
      !best ||
      match.score > best.score ||
      (match.score === best.score && match.windowStart < best.windowStart)
    ) {
      best = match;
    }
  }
  return best;
}

export function orderedCompactQueryTokenMatch(candidateTokens, queryValue) {
  return orderedCompactPreparedQueryTokenMatch(
    candidateTokens,
    preparedQueryMatchValue(queryValue).compact,
  );
}

function directQueryMatch(candidate, state) {
  const matchedValue = candidate.raw;
  if (!matchedValue) {
    return noMatch();
  }
  const candidateNormalized = candidate.normalized;
  const candidateSlug = candidate.slug;
  const candidateCompact = candidate.compact;
  const candidateTokens = candidate.tokens;

  if (
    (state.normalized && candidateNormalized === state.normalized) ||
    (state.slug && candidateSlug === state.slug)
  ) {
    return candidateMatch({
      rank: QUERY_MATCH_RANK_EXACT,
      score: 100000,
      matchMode: "exact",
      matchedValue,
      windowStart: candidateTokens.length ? 0 : null,
      windowEnd: candidateTokens.length || null,
      indexStart: 0,
      indexEnd: matchedValue.length,
    });
  }

  let best = noMatch();
  const prefixCandidates = [
    state.normalized && candidateNormalized.startsWith(state.normalized)
      ? { mode: "prefix", index: 0, length: state.normalized.length }
      : null,
    state.slug && candidateSlug.startsWith(state.slug)
      ? { mode: "slug-prefix", index: 0, length: state.slug.length }
      : null,
    state.compact && candidateCompact.startsWith(state.compact)
      ? { mode: "compact-prefix", index: 0, length: state.compact.length }
      : null,
  ].filter(Boolean);
  for (const prefix of prefixCandidates) {
    best = betterMatch(best, candidateMatch({
      rank: QUERY_MATCH_RANK_PREFIX,
      score: 80000 - Math.max(0, candidateCompact.length - prefix.length),
      matchMode: prefix.mode,
      matchedValue,
      windowStart: candidateTokens.length ? 0 : null,
      windowEnd: candidateTokens.length || null,
      indexStart: prefix.index,
      indexEnd: prefix.index + prefix.length,
    }));
  }
  if (best.rank >= QUERY_MATCH_RANK_PREFIX) {
    return best;
  }

  const substringCandidates = [
    state.normalized
      ? { mode: "substring", index: candidateNormalized.indexOf(state.normalized), length: state.normalized.length }
      : null,
    state.slug
      ? { mode: "slug-substring", index: candidateSlug.indexOf(state.slug), length: state.slug.length }
      : null,
    state.compact
      ? { mode: "compact-substring", index: candidateCompact.indexOf(state.compact), length: state.compact.length }
      : null,
  ].filter((entry) => entry && entry.index >= 0);
  for (const substring of substringCandidates) {
    best = betterMatch(best, candidateMatch({
      rank: QUERY_MATCH_RANK_SUBSTRING,
      score: 60000 - Math.min(substring.index, 200),
      matchMode: substring.mode,
      matchedValue,
      windowStart: null,
      windowEnd: null,
      indexStart: substring.index,
      indexEnd: substring.index + substring.length,
    }));
  }

  if (state.tokens.length > 1) {
    const tokenMatch = orderedPreparedQueryTokenMatch(
      candidate.compactTokens,
      state.compactTokens || state.tokens.map((token) => preparedQueryMatchValue(token).compact),
      { allowFuzzy: false },
    );
    if (tokenMatch) {
      best = betterMatch(best, candidateMatch({
        rank: QUERY_MATCH_RANK_SUBSTRING,
        score: 65000 + tokenMatch.score,
        matchMode: "token-window",
        matchedValue,
        windowStart: tokenMatch.windowStart,
        windowEnd: tokenMatch.windowEnd,
      }));
    }
  }

  const compactTokenMatch = orderedCompactPreparedQueryTokenMatch(
    candidateTokens,
    state.compact || "",
    candidate.compactTokens,
  );
  if (compactTokenMatch) {
    best = betterMatch(best, candidateMatch({
      rank: QUERY_MATCH_RANK_SUBSTRING,
      score: 66000 + compactTokenMatch.score,
      matchMode: "compact-token",
      matchedValue,
      windowStart: compactTokenMatch.windowStart,
      windowEnd: compactTokenMatch.windowEnd,
    }));
  }

  return best;
}

function fuzzyQueryMatch(candidate, state) {
  const matchedValue = candidate.raw;
  if (!matchedValue || !state.compact || state.compact.length < 4) {
    return noMatch();
  }
  const span = compactSubsequenceSpan(candidate.compact, state.compact);
  if (!span) {
    return noMatch();
  }
  return candidateMatch({
    rank: QUERY_MATCH_RANK_FUZZY,
    score:
      10000 +
      state.compact.length * 20 -
      Math.min(span.start * 4 + span.gapPenalty * 8 + span.spanPenalty * 5, 9000),
    matchMode: "fuzzy",
    matchedValue,
    indexStart: span.start,
    indexEnd: span.end,
  });
}

export function matchQueryValue(value, query, options = {}) {
  const state = typeof query === "object" && query
    ? query
    : prepareQueryMatchState(query, options);
  if (!state.normalized && !state.compact && !state.slug) {
    return noMatch();
  }
  const candidate = preparedQueryMatchValue(value);
  let best = directQueryMatch(candidate, state);
  if (best.rank < QUERY_MATCH_RANK_SUBSTRING && options.allowFuzzy !== false) {
    best = betterMatch(best, fuzzyQueryMatch(candidate, state));
  }
  return best && best.rank > QUERY_MATCH_RANK_NONE ? best : noMatch();
}

function matchPreparedQueryValuesWithState(preparedValues, state, options) {
  if (!state.normalized && !state.compact && !state.slug) {
    return noMatch();
  }
  let best = noMatch();
  preparedValues.forEach((candidate, index) => {
    let match = directQueryMatch(candidate, state);
    if (match.rank < QUERY_MATCH_RANK_SUBSTRING && options.allowFuzzy !== false) {
      match = betterMatch(match, fuzzyQueryMatch(candidate, state));
    }
    if (match.rank <= QUERY_MATCH_RANK_NONE) {
      return;
    }
    best = betterMatch(best, {
      ...match,
      score: match.score - index / 1000,
    });
  });
  return best && best.rank > QUERY_MATCH_RANK_NONE ? best : noMatch();
}

export function matchPreparedQueryValues(preparedValues, query, options = {}) {
  const state = typeof query === "object" && query
    ? query
    : prepareQueryMatchState(query, options);
  return matchPreparedQueryValuesWithState(
    Array.isArray(preparedValues) ? preparedValues : [],
    state,
    options,
  );
}

export function matchQueryValues(values, query, options = {}) {
  const cacheEntry = queryMatchValuesCacheEntry(values);
  const resultKey = queryMatchValuesResultKey(query, options);
  const cachedResult = cachedQueryMatchValuesResult(cacheEntry, resultKey);
  if (cachedResult) {
    return cachedResult;
  }
  const state = typeof query === "object" && query
    ? query
    : prepareQueryMatchState(query, options);
  const result = matchPreparedQueryValuesWithState(cacheEntry.preparedValues, state, options);
  cacheQueryMatchValuesResult(cacheEntry, resultKey, result);
  return result;
}

export function compareQueryMatches(left, right) {
  const better = betterMatch(left, right);
  if (better === left) {
    return -1;
  }
  if (better === right) {
    return 1;
  }
  return 0;
}
